import React from 'react';
import { GridColDef } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Handshake } from "@mui/icons-material";
import CustomDataGrid from '../../../../components/ui/customDataGrid/customDataGrid.component';
import { CONTRACTS_ENDPOINT } from '../../../../consts';
import styles from './clientContracts.module.css';
import { useHasPermission } from '../../../../contexts/auth';
import EntityReferenceLink from '../../../../components/ui/entityReference/entityReferenceLink.component';
import { ContractEndDateDisplay, formatContractEndDate } from '../../../../utils';
import {
  containsStringFilterOperators,
  dateRangeFilterOperators,
  equalsNumberFilterOperators,
  exactDateFilterOperators,
} from '../../../../components/ui/customDataGrid/filterOperators';

export const ClientContracts = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const canViewContracts = useHasPermission("settlement.view_contract");

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "ID",
      filterOperators: equalsNumberFilterOperators(),
    },
    {
      field: "number",
      headerName: t("Number"),
      filterOperators: containsStringFilterOperators(),
      renderCell: (params) => (
        <EntityReferenceLink
          entityType="contract"
          entityId={params.row.id}
          label={params.row.number || "-"}
          canPreview={canViewContracts}
        />
      ),
    },
    {
      field: "internal_name",
      headerName: t("Internal name"),
      filterOperators: containsStringFilterOperators(),
      valueGetter: (_value, row) => row.internal_name || "-",
    },
    {
        field: "start_date",
        headerName: t("Start Date"),
        filterOperators: dateRangeFilterOperators(),
    },
    {
        field: "end_date",
        headerName: t("End Date"),
        filterable: false,
        valueGetter: (value, row) =>
          formatContractEndDate(row.end_date, row.indefinite_after_end_date, t),
        renderCell: (params) => (
          <ContractEndDateDisplay
            endDate={params.row.end_date}
            indefiniteAfterEndDate={params.row.indefinite_after_end_date}
            t={t}
          />
        ),
    },
    {
      field: "signature_date",
      headerName: t("Signature Date"),
      editable: false,
      filterOperators: exactDateFilterOperators(),
    },
  ];

  const handleRowClick = (contractId: string) => {
    navigate(`/contracts/${contractId}`);
  };

  if (!canViewContracts) {
    return null;
  }

  const contractsUrl =`${CONTRACTS_ENDPOINT}?client=${id}`;

  return (
    <Box className={styles.container}>
      <CustomDataGrid
        icon={<Handshake />}
        title={t('Contracts')}
        url={contractsUrl}
        gridSettingsKey="CLIENT_CONTRACTS"
        columns={columns}
        onRowClick={handleRowClick}
        refreshEventName="clientContractAdded"
      />
    </Box>
  );
};
